/**
 * Servicing Transfer Notice & Payment Coupon PDF Generator
 *
 * Generates a professional 3-page PDF:
 *   Page 1: RESPA-compliant Notice of Servicing Transfer (12 CFR 1024.33)
 *   Page 2: Temporary Payment Coupon — Payment to Lendwise (current servicer)
 *   Page 3: Temporary Payment Coupon — Payment to New Servicer
 *
 * Follows jsPDF patterns from approval-letter and banker-recon generators.
 */

import jsPDF from 'jspdf';
import type { ServicingTransferNoticeData, ServicerContact, ServicingPayment } from '@/types/servicing';

// Colors matching Lendwise brand
const COLORS = {
  brandGreen: [22, 163, 74] as [number, number, number],
  bannerText: [255, 255, 255] as [number, number, number],
  black: [0, 0, 0] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  grayHeader: [240, 240, 240] as [number, number, number],
  grayText: [100, 100, 100] as [number, number, number],
  lightGray: [245, 245, 245] as [number, number, number],
  border: [180, 180, 180] as [number, number, number],
  warningBg: [255, 251, 235] as [number, number, number],
  warningBorder: [234, 179, 8] as [number, number, number],
  dashedLine: [150, 150, 150] as [number, number, number]
};

// Page dimensions (Letter size in points)
const PAGE = {
  width: 612,
  height: 792,
  marginLeft: 40,
  marginRight: 40,
  marginTop: 40,
  marginBottom: 50,
  get contentWidth() {
 return this.width - this.marginLeft - this.marginRight; 
}
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) {
return '--';
}
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) {
return '--';
}
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

function formatServicerAddress(s: ServicerContact): string[] {
  const lines: string[] = [];
  if (s.name) {
lines.push(s.name);
}
  if (s.department) {
lines.push(s.department);
}
  if (s.attentionTo) {
lines.push(`Attn: ${s.attentionTo}`);
}
  if (s.address) {
lines.push(s.address);
}
  if (s.address2) {
lines.push(s.address2);
}
  const csz = `${s.city}, ${s.state} ${s.zip}`.trim();
  if (csz.length > 2) {
lines.push(csz);
}
  return lines;
}

function ensureSpace(doc: jsPDF, y: number, needed: number): number {
  if (y + needed > PAGE.height - PAGE.marginBottom) {
    doc.addPage();
    return PAGE.marginTop;
  }
  return y;
}

// ---------------------------------------------------------------------------
// Page 1: Notice of Servicing Transfer
// ---------------------------------------------------------------------------

function drawNoticePage(doc: jsPDF, data: ServicingTransferNoticeData): void {
  let y = PAGE.marginTop;

  // --- Company Header ---
  doc.setFont('times', 'italic');
  doc.setFontSize(26);
  doc.setTextColor(...COLORS.black);
  doc.text('Lendwise', PAGE.marginLeft, y + 18);

  const lw = doc.getTextWidth('Lendwise');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setFillColor(160, 160, 160);
  doc.rect(PAGE.marginLeft + lw + 4, y + 4, 48, 16, 'F');
  doc.setTextColor(...COLORS.white);
  doc.text('MORTGAGE', PAGE.marginLeft + lw + 8, y + 14);

  // Date top-right
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.grayText);
  doc.text(formatDate(data.noticeDate), PAGE.width - PAGE.marginRight, y + 14, { align: 'right' });

  y += 32;

  // Separator line
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.line(PAGE.marginLeft, y, PAGE.width - PAGE.marginRight, y);
  y += 8;

  // --- Green Banner ---
  const bannerH = 26;
  doc.setFillColor(...COLORS.brandGreen);
  doc.rect(PAGE.marginLeft, y, PAGE.contentWidth, bannerH, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...COLORS.white);
  doc.text('NOTICE OF SERVICING TRANSFER', PAGE.width / 2, y + 17, { align: 'center' });
  y += bannerH + 12;

  // --- Borrower/Property/Loan # ---
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.black);

  const labelX = PAGE.marginLeft;
  const valueX = PAGE.marginLeft + 100;

  doc.setFont('helvetica', 'bold');
  doc.text('Borrower:', labelX, y);
  doc.setFont('helvetica', 'normal');
  doc.text(data.borrowerName + (data.coBorrowerName ? ` & ${data.coBorrowerName}` : ''), valueX, y);
  y += 14;

  doc.setFont('helvetica', 'bold');
  doc.text('Property Address:', labelX, y);
  doc.setFont('helvetica', 'normal');
  doc.text(data.propertyAddress.join(', ') || '--', valueX, y);
  y += 14;

  doc.setFont('helvetica', 'bold');
  doc.text('Loan #:', labelX, y);
  doc.setFont('helvetica', 'normal');
  doc.text(data.loanNumber, valueX, y);
  y += 18;

  // Separator
  doc.setDrawColor(...COLORS.border);
  doc.line(PAGE.marginLeft, y, PAGE.width - PAGE.marginRight, y);
  y += 10;

  // --- Opening Paragraph ---
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.black);

  const thankYouText = `Thank you for choosing ${data.currentServicer.name} for your mortgage financing needs!`;
  doc.text(thankYouText, PAGE.marginLeft, y, { maxWidth: PAGE.contentWidth });
  y += 16;

  const openingText = `${data.currentServicer.name} has been collecting your payments. ` +
    `The loan was sold on ${formatDate(data.loanSoldDate)} with servicing of your mortgage loan transferring ` +
    `effective ${formatDate(data.transferEffectiveDate)}. This means that as of this date, a new servicer will be ` +
    `collecting your mortgage loan payments from you. Nothing else about your mortgage loan will change.`;
  const openingLines = doc.splitTextToSize(openingText, PAGE.contentWidth);
  doc.text(openingLines, PAGE.marginLeft, y);
  y += openingLines.length * 11 + 8;

  // New servicer start notice
  doc.setFont('helvetica', 'bold');
  const newSvcNotice = `${data.newServicer.name} will be the new servicer and will start accepting payments received from you on ${formatDate(data.firstPaymentDateNewServicer)}.`;
  const nsnLines = doc.splitTextToSize(newSvcNotice, PAGE.contentWidth);
  doc.text(nsnLines, PAGE.marginLeft, y);
  y += nsnLines.length * 11 + 6;

  // ACH notice
  doc.setFont('helvetica', 'normal');
  const achText = 'Your ACH may have been revoked. If you currently have a completed ACH with ' +
    `${data.currentServicer.name}, please check with your new servicer to assure it was transferred ` +
    'with the loan or please complete a new ACH at the time of transfer.';
  const achLines = doc.splitTextToSize(achText, PAGE.contentWidth);
  doc.text(achLines, PAGE.marginLeft, y);
  y += achLines.length * 11 + 6;

  // Payment direction
  doc.setFont('helvetica', 'bold');
  const sendText = `Send all payments due on or after ${formatDate(data.firstPaymentDateNewServicer)} to ${data.newServicer.name} at this address:`;
  const sendLines = doc.splitTextToSize(sendText, PAGE.contentWidth);
  doc.text(sendLines, PAGE.marginLeft, y);
  y += sendLines.length * 11 + 8;

  // New servicer address block (centered)
  doc.setFont('helvetica', 'normal');
  const newSvcAddr = formatServicerAddress(data.newServicer);
  for (const line of newSvcAddr) {
    doc.text(line, PAGE.width / 2, y, { align: 'center' });
    y += 12;
  }
  if (data.newServicer.phone) {
    doc.text(`Phone: ${data.newServicer.phone}`, PAGE.width / 2, y, { align: 'center' });
    y += 12;
  }
  y += 6;

  // Past-due payments notice
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  const pastDueText = `Please note that payments due prior to the ${formatDate(data.firstPaymentDateNewServicer)} payment, ` +
    `if not yet paid, must be paid to your old servicer, ${data.currentServicer.name}.`;
  const pastDueLines = doc.splitTextToSize(pastDueText, PAGE.contentWidth);
  doc.text(pastDueLines, PAGE.marginLeft, y);
  y += pastDueLines.length * 10 + 12;

  // --- Two-column Servicer Contact ---
  y = ensureSpace(doc, y, 110);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const contactIntro = `If you have any questions for either your present servicer, ${data.currentServicer.name}, ` +
    `or your new servicer, ${data.newServicer.name}, about your mortgage loan or this transfer, ` +
    'please contact them using the information below:';
  const ciLines = doc.splitTextToSize(contactIntro, PAGE.contentWidth);
  doc.text(ciLines, PAGE.marginLeft, y);
  y += ciLines.length * 11 + 8;

  const colWidth = (PAGE.contentWidth - 16) / 2;
  const col1X = PAGE.marginLeft;
  const col2X = PAGE.marginLeft + colWidth + 16;

  // Column headers
  doc.setFillColor(...COLORS.grayHeader);
  doc.rect(col1X, y, colWidth, 16, 'F');
  doc.rect(col2X, y, colWidth, 16, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.black);
  doc.text(`Current Servicer Through ${formatDate(data.lastPaymentDateCurrentServicer)}`, col1X + 4, y + 11);
  doc.text(`New Servicer As Of ${formatDate(data.transferEffectiveDate)}`, col2X + 4, y + 11);
  y += 20;

  // Column content
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);

  const currentLines = [
    data.currentServicer.name,
    data.currentServicer.address,
    `${data.currentServicer.city}, ${data.currentServicer.state} ${data.currentServicer.zip}`,
    data.currentServicer.phone ? `Phone: ${data.currentServicer.phone}` : '',
    data.currentServicer.email ? `Email: ${data.currentServicer.email}` : ''
  ].filter(Boolean);

  const newLines = [
    data.newServicer.name,
    data.newServicer.attentionTo ? `Attn: ${data.newServicer.attentionTo}` : '',
    data.newServicer.address,
    `${data.newServicer.city}, ${data.newServicer.state} ${data.newServicer.zip}`,
    data.newServicer.phone ? `Phone: ${data.newServicer.phone}` : '',
    data.newServicer.hours ? `Hours: ${data.newServicer.hours}` : ''
  ].filter(Boolean);

  const maxLines = Math.max(currentLines.length, newLines.length);
  for (let i = 0; i < maxLines; i++) {
    const lineFont = i === 0 ? 'bold' : 'normal';
    doc.setFont('helvetica', lineFont);
    if (currentLines[i]) {
doc.text(currentLines[i], col1X + 4, y);
}
    if (newLines[i]) {
doc.text(newLines[i], col2X + 4, y);
}
    y += 11;
  }
  y += 8;

  // --- 60-Day Protection Notice ---
  y = ensureSpace(doc, y, 60);

  const protBoxH = 48;
  doc.setFillColor(...COLORS.warningBg);
  doc.setDrawColor(...COLORS.warningBorder);
  doc.setLineWidth(1);
  doc.rect(PAGE.marginLeft, y, PAGE.contentWidth, protBoxH, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.black);
  doc.text('IMPORTANT — 60-DAY PROTECTION', PAGE.marginLeft + 8, y + 12);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  const protText = 'Under Federal law, for 60 days from the date your loan is transferred, your new loan servicer cannot ' +
    'charge you a late fee or treat the payment as late if you sent the payment to ' +
    `${data.currentServicer.name} on time or within the applicable grace period.`;
  const protLines = doc.splitTextToSize(protText, PAGE.contentWidth - 16);
  doc.text(protLines, PAGE.marginLeft + 8, y + 24);
  y += protBoxH + 12;

  // Attached coupons notice
  y = ensureSpace(doc, y, 20);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('Please see attached Temporary Payment Coupons.', PAGE.marginLeft, y);
}

// ---------------------------------------------------------------------------
// Pages 2 & 3: Payment Coupons
// ---------------------------------------------------------------------------

function drawPaymentCoupon(
  doc: jsPDF,
  data: ServicingTransferNoticeData,
  payTo: ServicerContact,
  dueDate: string
): void {
  doc.addPage();
  let y = PAGE.marginTop;

  // --- Green Banner ---
  const bannerH = 26;
  doc.setFillColor(...COLORS.brandGreen);
  doc.rect(PAGE.marginLeft, y, PAGE.contentWidth, bannerH, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.white);
  doc.text('TEMPORARY PAYMENT COUPON', PAGE.width / 2, y + 17, { align: 'center' });
  y += bannerH + 4;

  // Sub-header
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.grayText);
  doc.text(data.currentServicer.name.toUpperCase(), PAGE.width / 2, y + 10, { align: 'center' });
  y += 18;

  // --- Loan Info Block ---
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.rect(PAGE.marginLeft, y, PAGE.contentWidth, 60, 'S');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.black);

  const infoLabelX = PAGE.marginLeft + 80;
  const infoValueX = PAGE.marginLeft + 160;
  let iy = y + 14;

  doc.setFont('helvetica', 'bold');
  doc.text('LOAN NO:', infoLabelX, iy);
  doc.setFont('helvetica', 'normal');
  doc.text(data.loanNumber, infoValueX, iy);
  iy += 13;

  doc.setFont('helvetica', 'bold');
  doc.text('DATE:', infoLabelX, iy);
  doc.setFont('helvetica', 'normal');
  doc.text(formatDate(data.noticeDate), infoValueX, iy);
  iy += 13;

  doc.setFont('helvetica', 'bold');
  doc.text('BORROWER:', infoLabelX, iy);
  doc.setFont('helvetica', 'normal');
  doc.text(data.borrowerName, infoValueX, iy);
  iy += 13;

  doc.setFont('helvetica', 'bold');
  doc.text('ADDRESS:', infoLabelX, iy);
  doc.setFont('helvetica', 'normal');
  doc.text(data.propertyAddress.join(', ') || '--', infoValueX, iy);

  y += 68;

  // --- Welcome Text ---
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(
    'We are pleased to have you as a mortgage loan customer. The following is a breakdown of your initial monthly payment:',
    PAGE.marginLeft,
    y,
    { maxWidth: PAGE.contentWidth }
  );
  y += 22;

  // --- Payment Breakdown ---
  const breakdownX = PAGE.marginLeft + 40;
  const amountX = PAGE.marginLeft + 260;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Principal and Interest:', breakdownX, y);
  doc.text(formatCurrency(data.principalAndInterest), amountX, y);
  y += 16;

  doc.text('Impounds:', breakdownX, y);
  doc.text(formatCurrency(data.escrowTotal), amountX, y);
  y += 14;

  // Escrow sub-breakdown
  if (data.hasEscrow && data.escrowBreakdown) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.text('Impounds Breakdown:', breakdownX + 16, y);
    y += 13;

    if (data.escrowBreakdown.insurance > 0) {
      doc.text('Insurance', breakdownX + 32, y);
      doc.text(formatCurrency(data.escrowBreakdown.insurance), amountX, y);
      y += 12;
    }

    if (data.escrowBreakdown.taxes > 0) {
      doc.text('Taxes', breakdownX + 32, y);
      doc.text(formatCurrency(data.escrowBreakdown.taxes), amountX, y);
      y += 12;
    }

    if (data.escrowBreakdown.mi > 0) {
      doc.text('Mortgage Insurance', breakdownX + 32, y);
      doc.text(formatCurrency(data.escrowBreakdown.mi), amountX, y);
      y += 12;
    }
  }

  y += 4;

  // Total
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('TOTAL MONTHLY PAYMENT:', breakdownX, y);
  doc.text(formatCurrency(data.totalMonthlyPayment), amountX, y);
  y += 18;

  // Due date
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Your regular payment is due `, PAGE.marginLeft, y);
  doc.setFont('helvetica', 'bold');
  doc.text(formatDate(dueDate), PAGE.marginLeft + doc.getTextWidth('Your regular payment is due '), y);
  y += 14;

  // Escrow change disclaimer
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.grayText);
  doc.text(
    'The total monthly payment may be subject to change each year due to increases or decreases in annual taxes and/or insurance premiums.',
    PAGE.marginLeft,
    y,
    { maxWidth: PAGE.contentWidth }
  );
  y += 24;

  // --- Dashed separator ---
  doc.setDrawColor(...COLORS.dashedLine);
  doc.setLineWidth(0.5);
  doc.setLineDashPattern([4, 3], 0);
  doc.line(PAGE.marginLeft, y, PAGE.width - PAGE.marginRight, y);
  doc.setLineDashPattern([], 0);
  y += 16;

  // --- Payment Stub ---
  doc.setTextColor(...COLORS.black);

  // Stub header row
  const stubCol1 = PAGE.marginLeft;
  const stubCol2 = PAGE.marginLeft + 180;
  const stubCol3 = PAGE.width - PAGE.marginRight - 140;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('MORTGAGE ACCOUNT NO.', stubCol2, y);
  doc.text('PAYMENT DUE DATE', stubCol3, y);
  y += 11;

  doc.setFont('helvetica', 'bold');
  doc.text(data.loanNumber, stubCol2, y);
  doc.text(formatDate(dueDate), stubCol3, y);
  y += 11;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('AMOUNT DUE', stubCol3, y);
  y += 11;
  doc.setFont('helvetica', 'bold');
  doc.text(formatCurrency(data.totalMonthlyPayment), stubCol3, y);
  y += 18;

  // Date/Check/AMT lines + Borrower name
  const lineStartX = stubCol1;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Date ________________________', lineStartX, y);
  doc.setFont('helvetica', 'bold');
  doc.text(data.borrowerName, stubCol2, y);
  y += 14;

  doc.setFont('helvetica', 'normal');
  doc.text('Check ________________________', lineStartX, y);
  doc.setFont('helvetica', 'bold');
  doc.text('Make check payable to:', stubCol2, y);

  // P&I / Escrow / Total on right
  const rightLabelX = stubCol3;
  const rightValX = PAGE.width - PAGE.marginRight;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('PRINCIPAL/INTEREST', rightLabelX, y);
  doc.text(formatCurrency(data.principalAndInterest), rightValX, y, { align: 'right' });
  y += 12;

  // Payable-to address
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('AMT ________________________', lineStartX, y);

  const payToLines = formatServicerAddress(payTo);
  let payToY = y - 6;
  doc.setFont('helvetica', 'normal');
  for (const line of payToLines) {
    payToY += 10;
    doc.text(line, stubCol2, payToY);
  }

  doc.text('OTHER', rightLabelX, y);
  y += 12;

  doc.text('ESCROW', rightLabelX, y);
  doc.text(formatCurrency(data.escrowTotal), rightValX, y, { align: 'right' });
  y += 12;

  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL', rightLabelX, y);
  doc.text(formatCurrency(data.totalMonthlyPayment), rightValX, y, { align: 'right' });
  y += 18;

  // Property address
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('PROPERTY ADDRESS', stubCol2, y);
  y += 11;
  doc.setFont('helvetica', 'normal');
  for (const line of data.propertyAddress) {
    doc.text(line, stubCol2, y);
    y += 10;
  }
  y += 10;

  // Footer
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('COUPON MUST ACCOMPANY PAYMENT', PAGE.width / 2, y, { align: 'center' });
}

// ---------------------------------------------------------------------------
// Page Numbers
// ---------------------------------------------------------------------------

function addPageNumbers(doc: jsPDF): void {
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.grayText);
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageW / 2,
      pageH - 20,
      { align: 'center' }
    );
    doc.text(
      'Generated by Lendwise',
      pageW - PAGE.marginRight,
      pageH - 20,
      { align: 'right' }
    );
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Generate the complete Servicing Transfer Notice PDF (3 pages).
 * Returns a Blob for download or preview.
 */
export function generateServicingTransferPdf(data: ServicingTransferNoticeData): Blob {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'letter'
  });

  // Page 1: Notice of Servicing Transfer
  drawNoticePage(doc, data);

  // Page 2: Payment Coupon — to Lendwise (current servicer)
  drawPaymentCoupon(doc, data, data.currentServicer, data.coupon1DueDate);

  // Page 3: Payment Coupon — to New Servicer
  drawPaymentCoupon(doc, data, data.newServicer, data.coupon2DueDate);

  // Page numbers
  addPageNumbers(doc);

  return doc.output('blob');
}

/**
 * Generate and trigger browser download of the PDF.
 */
export function downloadServicingTransferPdf(data: ServicingTransferNoticeData): void {
  const blob = generateServicingTransferPdf(data);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Servicing_Transfer_${data.loanNumber}_${data.noticeDate}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ===========================================================================
// Payment History PDF
// ===========================================================================

export interface PaymentHistoryData {
  loanNumber: string;
  borrowerName: string;
  loanAmount: number;
  interestRate: number;
  termMonths: number | null;
  closingDate: string | null;
  firstPaymentDate: string;
  investorPurchaseDate: string | null;
  firstPaymentToInvestor: string | null;
  investorName: string | null;
  investorLoanNumber: string | null;
  propertyAddress: string | null;
  propertyCity: string | null;
  propertyState: string | null;
  propertyZip: string | null;
  payments: ServicingPayment[];
  nextPaymentDueDate: string | null;
  generatedDate: string;
}

// Landscape page config for payment history (10-column table needs the width)
const PH_PAGE = {
  width: 792,
  height: 612,
  marginLeft: 40,
  marginRight: 40,
  marginTop: 40,
  marginBottom: 50,
  get contentWidth() {
 return this.width - this.marginLeft - this.marginRight; 
}
};

// Payment table column layout (10 columns in 712pt landscape content width)
const M = PH_PAGE.marginLeft;
const PH_COLS = {
  num:       { x: M,       w: 24,  label: '#',             align: 'center' as const },
  dueDate:   { x: M + 24,  w: 70,  label: 'Due Date',      align: 'left' as const },
  dateRcvd:  { x: M + 94,  w: 76,  label: 'Date Received',  align: 'left' as const },
  principal: { x: M + 170, w: 70,  label: 'Principal',      align: 'right' as const },
  interest:  { x: M + 240, w: 70,  label: 'Interest',       align: 'right' as const },
  escrow:    { x: M + 310, w: 62,  label: 'Escrow',         align: 'right' as const },
  totalDue:  { x: M + 372, w: 78,  label: 'Total Due',      align: 'right' as const },
  received:  { x: M + 450, w: 78,  label: 'Amt Received',   align: 'right' as const },
  balance:   { x: M + 528, w: 82,  label: 'Balance',        align: 'right' as const },
  status:    { x: M + 610, w: PH_PAGE.contentWidth - 610, label: 'Status', align: 'center' as const }
};

const PH_ROW_H = 16;
const PH_HEADER_H = 18;

function ensureSpacePH(doc: jsPDF, y: number, needed: number): number {
  if (y + needed > PH_PAGE.height - PH_PAGE.marginBottom) {
    doc.addPage();
    return PH_PAGE.marginTop;
  }
  return y;
}

function drawPaymentTableHeaders(doc: jsPDF, y: number): number {
  doc.setFillColor(...COLORS.grayHeader);
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.rect(PH_PAGE.marginLeft, y, PH_PAGE.contentWidth, PH_HEADER_H, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...COLORS.black);

  for (const col of Object.values(PH_COLS)) {
    const textX = col.align === 'right' ? col.x + col.w - 4
                : col.align === 'center' ? col.x + col.w / 2
                : col.x + 4;
    doc.text(col.label, textX, y + 12, { align: col.align });
  }

  return y + PH_HEADER_H;
}

function statusLabel(payment: ServicingPayment, today: string): string {
  if (payment.status === 'received') {
return 'Received';
}
  if (payment.status === 'bounced') {
return 'Bounced';
}
  if (payment.status === 'partial') {
return 'Partial';
}
  if (payment.status === 'waived') {
return 'Waived';
}
  if (payment.due_date < today) {
return 'Overdue';
}
  if (payment.status === 'due') {
return 'Due';
}
  return 'Scheduled';
}

function drawPaymentHistoryPage(doc: jsPDF, data: PaymentHistoryData): void {
  let y = PH_PAGE.marginTop;
  const today = data.generatedDate;

  // --- Company Header (matches transfer notice) ---
  doc.setFont('times', 'italic');
  doc.setFontSize(26);
  doc.setTextColor(...COLORS.black);
  doc.text('Lendwise', PH_PAGE.marginLeft, y + 18);

  const lw = doc.getTextWidth('Lendwise');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setFillColor(160, 160, 160);
  doc.rect(PH_PAGE.marginLeft + lw + 4, y + 4, 48, 16, 'F');
  doc.setTextColor(...COLORS.white);
  doc.text('MORTGAGE', PH_PAGE.marginLeft + lw + 8, y + 14);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.grayText);
  doc.text(formatDate(today), PH_PAGE.width - PH_PAGE.marginRight, y + 14, { align: 'right' });

  y += 32;

  // Separator
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.line(PH_PAGE.marginLeft, y, PH_PAGE.width - PH_PAGE.marginRight, y);
  y += 8;

  // --- Green Banner ---
  const bannerH = 26;
  doc.setFillColor(...COLORS.brandGreen);
  doc.rect(PH_PAGE.marginLeft, y, PH_PAGE.contentWidth, bannerH, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...COLORS.white);
  doc.text('PAYMENT HISTORY', PH_PAGE.width / 2, y + 17, { align: 'center' });
  y += bannerH + 12;

  // --- Loan Info Block ---
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.black);

  const labelX = PH_PAGE.marginLeft;
  const valueX = PH_PAGE.marginLeft + 110;
  const col2LabelX = PH_PAGE.width / 2 + 10;
  const col2ValueX = PH_PAGE.width / 2 + 120;

  const propertyAddr = [data.propertyAddress, data.propertyCity, data.propertyState, data.propertyZip]
    .filter(Boolean).join(', ') || '--';

  // Current balance = loan amount minus actual principal collected
  const totalPrincipalCollected = data.payments.reduce(
    (sum, p) => sum + (Number(p.principal_received) || 0), 0
  );
  const currentBalance = Math.round((data.loanAmount - totalPrincipalCollected) * 100) / 100;

  // Two-column info rows (label: value | label: value)
  const infoRows: [string, string, string, string][] = [
    ['Loan #:', data.loanNumber, 'Borrower:', data.borrowerName || '--'],
    ['Loan Amount:', formatCurrency(data.loanAmount), 'Current Balance:', formatCurrency(currentBalance)],
    ['Interest Rate:', `${data.interestRate.toFixed(3)}%`, 'Term:', data.termMonths ? `${data.termMonths} months` : '--'],
    ['Closing Date:', formatDate(data.closingDate), 'First Payment:', formatDate(data.firstPaymentDate)],
    ['Investor:', data.investorName || '--', 'Investor Loan #:', data.investorLoanNumber || '--'],
    ['Investor Purchase:', formatDate(data.investorPurchaseDate), '', '']
  ];

  for (const [l1, v1, l2, v2] of infoRows) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.grayText);
    doc.text(l1, labelX, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.black);
    doc.text(v1, valueX, y);
    if (l2) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.grayText);
      doc.text(l2, col2LabelX, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.black);
      doc.text(v2, col2ValueX, y);
    }
    y += 14;
  }

  // Property on its own full-width row to avoid overflow
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.grayText);
  doc.text('Property:', labelX, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.black);
  doc.text(propertyAddr, valueX, y, { maxWidth: PH_PAGE.width - PH_PAGE.marginRight - valueX });
  y += 14;

  y += 4;

  // Separator
  doc.setDrawColor(...COLORS.border);
  doc.line(PH_PAGE.marginLeft, y, PH_PAGE.width - PH_PAGE.marginRight, y);
  y += 10;

  // --- Payment Table ---
  // Filter to relevant payments (received, partial, due, late/overdue — skip future scheduled)
  const relevantPayments = data.payments.filter(p =>
    p.status === 'received' || p.status === 'partial' || p.status === 'due' || p.status === 'late' ||
    p.status === 'bounced' ||
    (p.status === 'scheduled' && p.due_date <= today)
  );

  y = drawPaymentTableHeaders(doc, y);

  // Running balance: starts at loan amount, reduced by actual principal received
  let runningBalance = data.loanAmount;

  for (const payment of relevantPayments) {
    // Page break check — redraw headers if needed
    y = ensureSpacePH(doc, y, PH_ROW_H);
    if (y === PH_PAGE.marginTop) {
      y = drawPaymentTableHeaders(doc, y);
    }

    // Update running balance with actual principal collected
    runningBalance -= Number(payment.principal_received) || 0;
    runningBalance = Math.round(runningBalance * 100) / 100;

    // Alternating row background
    if (payment.payment_number % 2 === 0) {
      doc.setFillColor(...COLORS.lightGray);
      doc.rect(PH_PAGE.marginLeft, y, PH_PAGE.contentWidth, PH_ROW_H, 'F');
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...COLORS.black);

    // #
    doc.text(
      String(payment.payment_number),
      PH_COLS.num.x + PH_COLS.num.w / 2, y + 11,
      { align: 'center' }
    );

    // Due Date
    doc.text(formatDate(payment.due_date), PH_COLS.dueDate.x + 4, y + 11);

    // Date Received — for bounced rows, fall back to the original (pre-reversal) receipt date
    const effectiveReceivedDate = payment.status === 'bounced'
      ? payment.reversed_payment_received_date
      : payment.payment_received_date;
    doc.text(
      effectiveReceivedDate ? formatDate(effectiveReceivedDate) : '--',
      PH_COLS.dateRcvd.x + 4, y + 11
    );

    // Principal
    doc.text(
      formatCurrency(Number(payment.principal_due) || 0),
      PH_COLS.principal.x + PH_COLS.principal.w - 4, y + 11,
      { align: 'right' }
    );

    // Interest
    doc.text(
      formatCurrency(Number(payment.interest_due) || 0),
      PH_COLS.interest.x + PH_COLS.interest.w - 4, y + 11,
      { align: 'right' }
    );

    // Escrow
    const escrowAmt = Number(payment.escrow_due) || 0;
    doc.text(
      escrowAmt > 0 ? formatCurrency(escrowAmt) : '--',
      PH_COLS.escrow.x + PH_COLS.escrow.w - 4, y + 11,
      { align: 'right' }
    );

    // Total Due
    doc.text(
      formatCurrency(Number(payment.total_amount_due) || 0),
      PH_COLS.totalDue.x + PH_COLS.totalDue.w - 4, y + 11,
      { align: 'right' }
    );

    // Amount Received — for bounced rows, render the original amount with a strikethrough
    const amtReceived = Number(payment.total_amount_received) || 0;
    const reversedAmt = Number(payment.reversed_total_amount_received) || 0;
    if (payment.status === 'bounced' && reversedAmt > 0) {
      const amtText = formatCurrency(reversedAmt);
      const rightX = PH_COLS.received.x + PH_COLS.received.w - 4;
      doc.text(amtText, rightX, y + 11, { align: 'right' });
      const textWidth = doc.getTextWidth(amtText);
      doc.setDrawColor(...COLORS.grayText);
      doc.setLineWidth(0.6);
      doc.line(rightX - textWidth, y + 8, rightX, y + 8);
    } else {
      doc.text(
        amtReceived > 0 ? formatCurrency(amtReceived) : '--',
        PH_COLS.received.x + PH_COLS.received.w - 4, y + 11,
        { align: 'right' }
      );
    }

    // Balance
    doc.text(
      formatCurrency(runningBalance),
      PH_COLS.balance.x + PH_COLS.balance.w - 4, y + 11,
      { align: 'right' }
    );

    // Status — two-line for bounced (label + reversal date), single-line otherwise
    const label = statusLabel(payment, today);
    const statusColor = label === 'Received' ? COLORS.brandGreen
                       : label === 'Overdue' ? [220, 38, 38] as [number, number, number]
                       : label === 'Bounced' ? [220, 38, 38] as [number, number, number]
                       : label === 'Partial' ? [217, 119, 6] as [number, number, number]
                       : COLORS.grayText;
    const statusCenterX = PH_COLS.status.x + PH_COLS.status.w / 2;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...statusColor);
    if (label === 'Bounced' && payment.reversed_at) {
      doc.setFontSize(7.5);
      doc.text(label, statusCenterX, y + 8, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(...COLORS.grayText);
      doc.text(
        `Reversed ${formatDate(payment.reversed_at.slice(0, 10))}`,
        statusCenterX, y + 15,
        { align: 'center' }
      );
      doc.setFontSize(7.5);
    } else {
      doc.text(label, statusCenterX, y + 11, { align: 'center' });
    }

    y += PH_ROW_H;
  }

  // Bottom border of table
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.line(PH_PAGE.marginLeft, y, PH_PAGE.width - PH_PAGE.marginRight, y);
  y += 16;

  // --- Summary Footer ---
  y = ensureSpacePH(doc, y, 60);

  // Find last received payment
  const receivedPayments = data.payments.filter(p => p.status === 'received');
  const lastReceived = receivedPayments.length > 0
    ? receivedPayments[receivedPayments.length - 1]
    : null;

  const totalCollected = receivedPayments.reduce(
    (sum, p) => sum + (Number(p.total_amount_received) || 0), 0
  );

  // Summary box
  const summaryH = 56;
  doc.setFillColor(...COLORS.lightGray);
  doc.setDrawColor(...COLORS.border);
  doc.rect(PH_PAGE.marginLeft, y, PH_PAGE.contentWidth, summaryH, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.black);

  const sLabelX = PH_PAGE.marginLeft + 12;
  const sValueX = PH_PAGE.marginLeft + 200;
  let sy = y + 14;

  doc.text('Last Payment Collected:', sLabelX, sy);
  doc.setFont('helvetica', 'normal');
  doc.text(
    lastReceived
      ? `${formatDate(lastReceived.payment_received_date || lastReceived.due_date)} — ${formatCurrency(Number(lastReceived.total_amount_received) || 0)}`
      : 'No payments collected',
    sValueX, sy
  );

  sy += 16;
  doc.setFont('helvetica', 'bold');
  doc.text('Next Payment Due:', sLabelX, sy);
  doc.setFont('helvetica', 'normal');
  // If all serviced payments are received, show first payment to investor date
  const nextDue = data.nextPaymentDueDate || data.firstPaymentToInvestor;
  doc.text(nextDue ? formatDate(nextDue) : 'N/A', sValueX, sy);

  sy += 16;
  doc.setFont('helvetica', 'bold');
  doc.text('Total Collected:', sLabelX, sy);
  doc.setFont('helvetica', 'normal');
  doc.text(formatCurrency(totalCollected), sValueX, sy);
}

/**
 * Generate Payment History PDF. Returns a Blob for download.
 */
export function generatePaymentHistoryPdf(data: PaymentHistoryData): Blob {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'letter'
  });

  drawPaymentHistoryPage(doc, data);
  addPageNumbers(doc);

  return doc.output('blob');
}

/**
 * Generate and trigger browser download of the Payment History PDF.
 */
export function downloadPaymentHistoryPdf(data: PaymentHistoryData): void {
  const blob = generatePaymentHistoryPdf(data);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Payment_History_${data.loanNumber}_${data.generatedDate}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
